/**
 * Utilidades para impresión térmica vía Bluetooth usando Web Bluetooth API
 * Compatible con impresoras térmicas ESC/POS estándar
 */

// Comandos ESC/POS estándar para impresoras térmicas
const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;
const ESC_INIT = [ESC, 0x40]; // Inicializar impresora
const ESC_ALIGN_CENTER = [ESC, 0x61, 0x01]; // Alinear centro
const ESC_ALIGN_LEFT = [ESC, 0x61, 0x00]; // Alinear izquierda
const ESC_ALIGN_RIGHT = [ESC, 0x61, 0x02]; // Alinear derecha
const ESC_BOLD_ON = [ESC, 0x45, 0x01]; // Negrita ON
const ESC_BOLD_OFF = [ESC, 0x45, 0x00]; // Negrita OFF
const ESC_DOUBLE_HEIGHT = [GS, 0x21, 0x11]; // Texto doble altura
const ESC_NORMAL_TEXT = [GS, 0x21, 0x00]; // Texto normal
const ESC_CUT_PAPER = [GS, 0x56, 0x00]; // Cortar papel
const ESC_FEED_LINES = [ESC, 0x64, 0x03]; // Avanzar 3 líneas

class BluetoothThermalPrinter {
  constructor() {
    this.device = null;
    this.characteristic = null;
    this.connected = false;
  }

  /**
   * Verificar si Web Bluetooth está disponible
   */
  static isSupported() {
    return 'bluetooth' in navigator;
  }

  /**
   * Intentar reconectar automáticamente a dispositivos previamente emparejados
   * Prioriza la impresora "58HB6" si está disponible
   */
  async autoReconnect(targetPrinterName = '58HB6') {
    try {
      if (!BluetoothThermalPrinter.isSupported()) {
        throw new Error('Web Bluetooth no está soportado');
      }

      console.log(`🔄 Intentando reconexión automática a: ${targetPrinterName}...`);

      // Verificar si la API getDevices está disponible (experimental)
      if (!navigator.bluetooth.getDevices) {
        console.log('⚠️ API getDevices() no disponible');
        console.log('💡 Solución: Click manual en botón Bluetooth para conectar');
        return { success: false, reason: 'api_not_available' };
      }

      // Obtener dispositivos previamente emparejados
      const devices = await navigator.bluetooth.getDevices();
      
      if (devices.length === 0) {
        console.log('⚠️ No hay dispositivos previamente emparejados');
        console.log('💡 Primera vez: Conecta manualmente para emparejar');
        return { success: false, reason: 'no_paired_devices' };
      }

      console.log(`📱 Dispositivos emparejados encontrados: ${devices.length}`);
      devices.forEach((d, i) => console.log(`  ${i + 1}. ${d.name || 'Sin nombre'}`));

      // Buscar específicamente la impresora "58HB6"
      let targetDevice = devices.find(d => d.name && d.name.includes(targetPrinterName));
      
      // Si no se encuentra, usar el primer dispositivo emparejado
      if (!targetDevice) {
        console.log(`⚠️ Impresora "${targetPrinterName}" no encontrada, usando primer dispositivo`);
        targetDevice = devices[0];
      } else {
        console.log(`✅ Impresora "${targetPrinterName}" encontrada!`);
      }

      this.device = targetDevice;
      console.log(`🔄 Conectando a: ${this.device.name || 'Impresora'}`);

      // Intentar conectar
      const result = await this.connectToDevice(this.device);
      
      if (result.success) {
        console.log('✅ Reconexión automática exitosa');
        localStorage.setItem('printer_name', this.device.name || 'Impresora');
        localStorage.setItem('printer_auto_reconnect', 'true');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Error en auto-reconexión:', error);
      return { success: false, reason: 'error', error: error.message };
    }
  }

  /**
   * Conectar rápidamente a una impresora específica por nombre
   * Filtra la lista de dispositivos para mostrar solo la impresora objetivo
   */
  async connectToSpecificPrinter(printerName = '58HB6') {
    try {
      if (!BluetoothThermalPrinter.isSupported()) {
        throw new Error('Web Bluetooth no está soportado en este navegador. Usa Chrome o Edge.');
      }

      console.log(`🔍 Buscando impresora: ${printerName}...`);
      
      // Solicitar dispositivo con filtro por nombre
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: printerName },
          { name: printerName }
        ],
        optionalServices: [
          0x18f0,
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          '0000ff00-0000-1000-8000-00805f9b34fb',
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
        ]
      });

      console.log(`✓ Impresora seleccionada: ${this.device.name}`);
      localStorage.setItem('printer_name', this.device.name || 'Impresora');
      localStorage.setItem('printer_auto_reconnect', 'true');

      return await this.connectToDevice(this.device);
    } catch (error) {
      // Si falla con filtro, intentar sin filtro (mostrar todas)
      if (error.message.includes('no devices found')) {
        console.log(`⚠️ Impresora "${printerName}" no encontrada, mostrando todas...`);
        return await this.connect();
      }
      throw error;
    }
  }

  /**
   * Conectar a una impresora térmica Bluetooth
   */
  async connect() {
    try {
      if (!BluetoothThermalPrinter.isSupported()) {
        throw new Error('Web Bluetooth no está soportado en este navegador. Usa Chrome o Edge.');
      }

      console.log('🔍 Buscando impresoras Bluetooth...');
      
      // Solicitar dispositivo Bluetooth SIN FILTROS para máxima compatibilidad
      // Esto mostrará TODOS los dispositivos Bluetooth disponibles
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          0x18f0, // Thermal Printer Service
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // SPP Service UUID común
          '0000ff00-0000-1000-8000-00805f9b34fb', // Otro servicio común
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // BLE Printer Service alternativo
        ]
      });

      console.log('✓ Dispositivo seleccionado:', this.device.name);
      
      // Guardar nombre en localStorage para futuras reconexiones
      localStorage.setItem('printer_name', this.device.name || 'Impresora');

      return await this.connectToDevice(this.device);
    } catch (error) {
      console.error('❌ Error conectando a impresora:', error);
      this.connected = false;
      throw error;
    }
  }

  /**
   * Conectar a un dispositivo específico (helper interno)
   */
  async connectToDevice(device) {
    try {
      // Conectar al servidor GATT
      const server = await device.gatt.connect();
      console.log('✓ Conectado al servidor GATT');

      // Buscar servicio y característica de escritura en todos los servicios disponibles
      let service;
      const services = await server.getPrimaryServices();
      console.log(`✓ Encontrados ${services.length} servicios`);
      
      // Buscar en todos los servicios hasta encontrar una característica de escritura
      for (const srv of services) {
        try {
          const characteristics = await srv.getCharacteristics();
          const writeChar = characteristics.find(c => 
            c.properties.write || 
            c.properties.writeWithoutResponse
          );
          
          if (writeChar) {
            this.characteristic = writeChar;
            service = srv;
            console.log(`✓ Característica de escritura encontrada en servicio: ${srv.uuid}`);
            break;
          }
        } catch (e) {
          // Continuar con el siguiente servicio
          continue;
        }
      }

      if (!this.characteristic) {
        throw new Error('No se encontró una característica de escritura. Esta impresora puede no ser compatible.');
      }

      console.log('✓ Característica de escritura obtenida');
      this.connected = true;

      return {
        success: true,
        deviceName: device.name || 'Impresora'
      };
    } catch (error) {
      console.error('❌ Error conectando al dispositivo:', error);
      this.connected = false;
      throw error;
    }
  }

  /**
   * Desconectar de la impresora
   */
  disconnect() {
    if (this.device && this.device.gatt.connected) {
      this.device.gatt.disconnect();
      this.connected = false;
      console.log('✓ Impresora desconectada');
    }
  }

  /**
   * Enviar datos raw a la impresora
   */
  async sendRawData(data) {
    if (!this.connected || !this.characteristic) {
      throw new Error('Impresora no conectada. Conecta primero usando connect()');
    }

    try {
      const uint8Array = new Uint8Array(data);
      
      // Las impresoras Bluetooth tienen un límite de bytes por paquete (generalmente 20-512)
      // Dividimos en chunks de 100 bytes para mayor compatibilidad
      const chunkSize = 100;
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        await this.characteristic.writeValue(chunk);
        // Pequeña pausa entre chunks para evitar overflow del buffer
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error('❌ Error enviando datos a impresora:', error);
      throw error;
    }
  }

  /**
   * Convertir texto a bytes usando codificación ISO-8859-1 (compatible con ESC/POS)
   */
  textToBytes(text) {
    const encoder = new TextEncoder();
    // Reemplazar caracteres especiales comunes del español
    const normalized = text
      .replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i')
      .replace(/ó/g, 'o').replace(/ú/g, 'u').replace(/ñ/g, 'n')
      .replace(/Á/g, 'A').replace(/É/g, 'E').replace(/Í/g, 'I')
      .replace(/Ó/g, 'O').replace(/Ú/g, 'U').replace(/Ñ/g, 'N');
    
    return Array.from(encoder.encode(normalized));
  }

  /**
   * Imprimir un ticket de venta
   */
  async printTicket(ticketData) {
    const commands = [];

    // Inicializar impresora
    commands.push(...ESC_INIT);

    // Logo / Encabezado
    commands.push(...ESC_ALIGN_CENTER);
    commands.push(...ESC_DOUBLE_HEIGHT, ...ESC_BOLD_ON);
    commands.push(...this.textToBytes('BUENAS COMPRAS'));
    commands.push(LF);
    commands.push(...ESC_NORMAL_TEXT, ...ESC_BOLD_OFF);
    commands.push(...this.textToBytes('Tu tienda de confianza'));
    commands.push(LF);
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(LF);

    // Información del ticket
    commands.push(...ESC_ALIGN_LEFT);
    commands.push(...this.textToBytes(`Ticket: #${ticketData.ticket_number}`));
    commands.push(LF);
    commands.push(...this.textToBytes(`Fecha: ${ticketData.fecha}`));
    commands.push(LF);
    commands.push(...this.textToBytes(`Vendedor: ${ticketData.vendedor_name}`));
    commands.push(LF);
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(LF);

    // Productos
    commands.push(...ESC_BOLD_ON);
    commands.push(...this.textToBytes('PRODUCTOS:'));
    commands.push(...ESC_BOLD_OFF);
    commands.push(LF);

    ticketData.items.forEach(item => {
      // Nombre del producto
      commands.push(...this.textToBytes(item.product_name));
      commands.push(LF);
      
      // Nicho (pequeño, indentado)
      if (item.nicho_name) {
        commands.push(...this.textToBytes(`  ${item.nicho_name}`));
        commands.push(LF);
      }
      
      // Precio (alineado a la derecha)
      const priceText = `$${item.amount.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      const spaces = ' '.repeat(Math.max(0, 32 - priceText.length));
      commands.push(...this.textToBytes(spaces + priceText));
      commands.push(LF);
    });

    // Separador
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(LF);

    // Total
    commands.push(...ESC_DOUBLE_HEIGHT, ...ESC_BOLD_ON);
    const totalText = `TOTAL: $${ticketData.total_amount.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    commands.push(...this.textToBytes(totalText));
    commands.push(LF);
    commands.push(...ESC_NORMAL_TEXT, ...ESC_BOLD_OFF);

    // Separador
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(LF);

    // Contacto
    commands.push(...ESC_ALIGN_CENTER);
    commands.push(...this.textToBytes('WhatsApp: +5491164070339'));
    commands.push(LF);
    commands.push(...this.textToBytes('buenascompras.end@gmail.com'));
    commands.push(LF, LF);
    commands.push(...this.textToBytes('Gracias por tu compra!'));
    commands.push(LF);
    commands.push(...this.textToBytes('Te esperamos pronto'));
    commands.push(LF);

    // Avanzar papel y cortar
    commands.push(...ESC_FEED_LINES);
    commands.push(...ESC_CUT_PAPER);

    // Enviar a impresora
    await this.sendRawData(commands);
    console.log('✓ Ticket enviado a impresora');
  }
}

// Instancia global
let printerInstance = null;

export const getThermalPrinter = () => {
  if (!printerInstance) {
    printerInstance = new BluetoothThermalPrinter();
  }
  return printerInstance;
};

export const isBluetoothSupported = BluetoothThermalPrinter.isSupported;

export default BluetoothThermalPrinter;
